import React, { useEffect, useState } from 'react';
import { Button, Container, Dropdown, Nav, Navbar } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiBookOpen,
  FiGrid,
  FiHeart,
  FiHelpCircle,
  FiInfo,
  FiLogOut,
  FiMail,
  FiUser,
} from 'react-icons/fi';
import Logo from './common/Logo';
import { useAuth } from '../context/AuthContext';

// Ordered to follow the page: browse the conditions, read about us, dig into
// the wiki, check the questions, then get in touch.
const NAV_LINKS = [
  { label: 'Conditions', href: '#categories', Icon: FiGrid },
  { label: 'About', href: '#about', Icon: FiInfo },
  { label: 'Wiki', href: '#wiki', Icon: FiBookOpen },
  { label: 'FAQ', href: '#faq', Icon: FiHelpCircle },
  { label: 'Connect', href: '#connect', Icon: FiMail },
];

/** Two-letter monogram for the account chip, e.g. "Priya Sharma" -> "PS". */
function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // The navbar is translucent over the aurora backdrop; once content scrolls
  // beneath it, it needs more opacity and a shadow to stay legible.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Navbar
      expand="lg"
      sticky="top"
      className={`mm-navbar ${scrolled ? 'mm-navbar--scrolled' : ''}`}
    >
      <Container>
        <Navbar.Brand as={Link} to="/" className="mm-navbar__brand p-0">
          <Logo tagline="Your safe space to heal" markSize={38} />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="mm-nav" />

        {/* At lg+ the collapse becomes `display: contents` so these two blocks
            drop into the container's 1fr / auto / 1fr grid. Equal side columns
            are what centres the links against the bar rather than against the
            space left over between the brand and the buttons. */}
        <Navbar.Collapse id="mm-nav">
          <Nav className="mm-navbar__links">
            {NAV_LINKS.map(({ label, href, Icon }) => (
              <Nav.Link key={label} href={href} className="mm-navbar__link">
                <Icon size={15} aria-hidden="true" />
                {label}
              </Nav.Link>
            ))}
          </Nav>

          <div className="mm-navbar__actions">
            {isAuthenticated ? (
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="link"
                  id="mm-account-menu"
                  className="mm-navbar__login text-decoration-none p-1"
                >
                  <span className="mm-account">
                    <span className="mm-account__avatar" aria-hidden="true">
                      {initials(user?.name)}
                    </span>
                    <span className="mm-account__name">{user?.name}</span>
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow border-0 mt-2">
                  <Dropdown.Header className="text-truncate">
                    {user?.email}
                  </Dropdown.Header>
                  <Dropdown.Divider />
                  {user?.role === 'admin' && (
                    <>
                      <Dropdown.Item as={Link} to="/admin">
                        <FiGrid size={14} className="me-2" aria-hidden="true" />
                        Admin dashboard
                      </Dropdown.Item>
                      <Dropdown.Divider />
                    </>
                  )}
                  {user?.role === 'clinician' && (
                    <>
                      <Dropdown.Item as={Link} to="/clinician">
                        <FiGrid size={14} className="me-2" aria-hidden="true" />
                        Clinician portal
                      </Dropdown.Item>
                      <Dropdown.Divider />
                    </>
                  )}
                  <Dropdown.Item href="#account">
                    <FiUser size={14} className="me-2" aria-hidden="true" />
                    My account
                  </Dropdown.Item>
                  <Dropdown.Item href="#saved">
                    <FiHeart size={14} className="me-2" aria-hidden="true" />
                    Saved therapists
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>
                    <FiLogOut size={14} className="me-2" aria-hidden="true" />
                    Log out
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <Link to="/login" className="mm-navbar__login">
                Log In
                <FiArrowRight size={15} aria-hidden="true" />
              </Link>
            )}

            <Button
              as={Link}
              to={isAuthenticated ? '/#search' : '/signup'}
              className="mm-navbar__cta"
            >
              Find a therapist
            </Button>
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}
